/**
 * 時間軸カレンダー（15 分刻み）の計算。
 *
 * 週・日表示を Google カレンダーと同じ「縦が時刻」のグリッドにするための計算をまとめる。
 * 目盛りは 15 分刻み。経理の予定は 30 分・1 時間が多いが、15 分の打ち合わせや
 * 締めの細かい作業が潰れて見えなくなるため、最小単位を 15 分に取る。
 *
 * 描画は px に落とす必要があるので、ここで「15 分 = 何 px か」も決めておく。
 * 高さの計算が画面側に散らないよう、位置とサイズの算出は全てこのモジュールに置く。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 時間軸カレンダーの目盛り・タスクの縦位置・重なったタスクの横並びを計算する。
 * @graph-connects none
 */
import type { Task } from './task.js';

/** 目盛りの最小単位（分） */
/** @graph-connects none */
export const SLOT_MINUTES = 15;

/**
 * 15 分ぶんの高さ（px）。1 時間 48px になり、24 時間で 1,152px。
 *
 * Google カレンダーの既定の高さに合わせる。15 分の予定は 1 行（内容、時刻）で出すので、
 * この高さでも読める。
 */
/** @graph-connects none */
export const SLOT_HEIGHT_PX = 12;

/** 1 時間ぶんの高さ（px） */
/** @graph-connects none */
export const HOUR_HEIGHT_PX = (60 / SLOT_MINUTES) * SLOT_HEIGHT_PX;

/** 1 日ぶんの高さ（px） */
/** @graph-connects none */
export const DAY_HEIGHT_PX = 24 * HOUR_HEIGHT_PX;

/** 1 分ぶんの高さ（px） */
/** @graph-connects none */
export const MINUTE_HEIGHT_PX = HOUR_HEIGHT_PX / 60;

/**
 * 終了時刻が無いタスクの既定の長さ（分）。
 *
 * カレンダー取り込みでは終了時刻が入るが、手で作ったタスクには無いことがある。
 * 1 枠（15 分）だと文字が入らず読めないので、2 枠ぶんを確保する。
 */
/** @graph-connects none */
export const DEFAULT_DURATION_MINUTES = 30;

/** 最初に見せる時刻。0 時から見せても空欄しか映らないため、始業前後へ寄せる */
/** @graph-connects none */
export const DEFAULT_SCROLL_HOUR = 8;

/** 0〜23 時の目盛り */
/** @graph-connects none */
export const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business `HH:MM` を 0 時からの分に直す。時刻として読めない値は null を返す。
 * @graph-connects none
 */
export function parseTime(hhmm: string | undefined): number | null {
  if (hhmm === undefined) {
    return null;
  }
  const matched = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (matched === null) {
    return null;
  }
  const hour = Number(matched[1]);
  const minute = Number(matched[2]);
  if (hour > 23 || minute > 59) {
    return null;
  }
  return hour * 60 + minute;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 0 時からの分を `HH:MM` に直す。
 * @graph-connects none
 */
export function formatTime(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 目盛りの時刻表記を返す。Google カレンダーと同じ「午前10時 / 午後1時」の形。
 * @graph-connects none
 */
export function formatHourLabel(hour: number): string {
  if (hour < 12) {
    return `午前${String(hour)}時`;
  }
  return `午後${String(hour === 12 ? 12 : hour - 12)}時`;
}

/**
 * 時間軸に置いたタスク 1 件。
 */
export interface PositionedTask {
  /** 元のタスク */
  task: Task;
  /** 開始（0 時からの分） */
  startMinutes: number;
  /** 終了（0 時からの分） */
  endMinutes: number;
  /** 重なり合うタスクの中での横位置（0 始まり） */
  lane: number;
  /** 重なり合うタスクの数。横幅は 1/laneCount になる */
  laneCount: number;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 時刻を持つタスクと、終日として扱うタスクに分ける。時刻が読めない値は終日に寄せる。
 * @graph-connects parseTime [calls] 開始時刻を分に直す
 */
export function splitTimedTasks(tasks: readonly Task[]): { timed: Task[]; allDay: Task[] } {
  const timed: Task[] = [];
  const allDay: Task[] = [];
  for (const task of tasks) {
    if (parseTime(task.startTime) === null) {
      allDay.push(task);
    } else {
      timed.push(task);
    }
  }
  return { timed, allDay };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 時刻を持つタスクを時間軸に配置する。重なっている分は横に並べ、重なりの塊ごとに横幅を決める。
 * @graph-connects parseTime [calls] 時刻を分に直す
 */
export function layoutTimedTasks(tasks: readonly Task[]): PositionedTask[] {
  const spans = tasks
    .flatMap((task) => {
      const startMinutes = parseTime(task.startTime);
      if (startMinutes === null) {
        return [];
      }
      const parsedEnd = parseTime(task.endTime);
      // 終了が無い・開始以前になっている場合は既定の長さを足す（0 の高さを作らない）
      const endMinutes =
        parsedEnd === null || parsedEnd <= startMinutes
          ? startMinutes + DEFAULT_DURATION_MINUTES
          : parsedEnd;
      return [{ task, startMinutes, endMinutes }];
    })
    // 開始が早い順。同時開始なら長いものを左に置く（短い予定が奥に隠れない）
    .sort(
      (a, b) =>
        a.startMinutes - b.startMinutes ||
        b.endMinutes - b.startMinutes - (a.endMinutes - a.startMinutes),
    );

  const positioned: PositionedTask[] = [];
  // 重なりが途切れるまでを 1 つの塊として扱い、塊の中でだけ横幅を分け合う
  let cluster: PositionedTask[] = [];
  let laneEnds: number[] = [];

  const flush = () => {
    for (const item of cluster) {
      item.laneCount = laneEnds.length;
      positioned.push(item);
    }
    cluster = [];
    laneEnds = [];
  };

  for (const span of spans) {
    if (cluster.length > 0 && laneEnds.every((end) => end <= span.startMinutes)) {
      flush();
    }
    // 空いている列のうち最も左を使う
    let lane = laneEnds.findIndex((end) => end <= span.startMinutes);
    if (lane === -1) {
      lane = laneEnds.length;
    }
    laneEnds[lane] = span.endMinutes;
    cluster.push({ ...span, lane, laneCount: 1 });
  }
  flush();

  return positioned;
}

/**
 * Google カレンダー風に置いた予定 1 件。横位置は列に対する割合（%）で持つ。
 */
export interface CalendarEventBox {
  /** 元のタスク */
  task: Task;
  /** 開始（0 時からの分） */
  startMinutes: number;
  /** 終了（0 時からの分） */
  endMinutes: number;
  /** 左端（列幅に対する %） */
  left: number;
  /** 幅（列幅に対する %） */
  width: number;
  /** 重なりの深さ。深いものほど手前に描く */
  depth: number;
}

/** 開始がこの分数以内なら「同時に始まる予定」として横に並べる。離れていれば手前に重ねる */
/** @graph-connects none */
export const SIDE_BY_SIDE_MINUTES = 45;

/** 重ねるときに右へずらす量（列幅に対する %） */
/** @graph-connects none */
export const NEST_INDENT_PERCENT = 12;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 時刻を持つタスクを Google カレンダーと同じ考え方で置く。ほぼ同時に始まる予定は横に並べ、後から始まる予定は少し右へずらして手前に重ねる（長い予定に挟まれても、短い予定の幅が細くならない）
 * @graph-connects layoutTimedTasks [calls] 開始・終了の分と並び順を揃える
 */
export function layoutCalendarEvents(tasks: readonly Task[]): CalendarEventBox[] {
  const placed: CalendarEventBox[] = [];
  for (const { task, startMinutes, endMinutes } of layoutTimedTasks(tasks).sort(
    (a, b) =>
      a.startMinutes - b.startMinutes ||
      b.endMinutes - b.startMinutes - (a.endMinutes - a.startMinutes),
  )) {
    const overlapping = placed.filter(
      (box) => box.startMinutes < endMinutes && box.endMinutes > startMinutes,
    );
    const siblings = overlapping.filter(
      (box) => Math.abs(box.startMinutes - startMinutes) < SIDE_BY_SIDE_MINUTES,
    );
    if (siblings.length > 0) {
      // ほぼ同時に始まる予定どうしは、最初の予定が持っていた幅を等分して横に並べる
      const box: CalendarEventBox = { task, startMinutes, endMinutes, left: 0, width: 0, depth: 0 };
      const group = [...siblings, box];
      const base = Math.min(...siblings.map((box) => box.left));
      const depth = Math.max(...siblings.map((box) => box.depth));
      const width = (100 - base) / group.length;
      group.forEach((box, index) => {
        box.left = base + index * width;
        box.width = width;
        box.depth = depth;
      });
      placed.push(box);
      continue;
    }
    // 後から始まる予定は、いちばん手前の重なりから少し右へずらして重ねる
    const parent = overlapping.reduce<CalendarEventBox | null>(
      (top, box) => (top === null || box.depth >= top.depth ? box : top),
      null,
    );
    const left = parent === null ? 0 : Math.min(parent.left + NEST_INDENT_PERCENT, 75);
    placed.push({
      task,
      startMinutes,
      endMinutes,
      left,
      width: 100 - left,
      depth: parent === null ? 0 : parent.depth + 1,
    });
  }
  return placed;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの時刻表記を返す。時刻を持たないタスクは空文字を返す。
 * @graph-connects parseTime [calls] 時刻を分に直す
 * @graph-connects formatTime [calls] 分を `HH:MM` に戻す
 */
export function formatTaskTimeRange(task: Task): string {
  const startMinutes = parseTime(task.startTime);
  if (startMinutes === null) {
    return '';
  }
  const parsedEnd = parseTime(task.endTime);
  if (parsedEnd === null || parsedEnd <= startMinutes) {
    return formatTime(startMinutes);
  }
  return `${formatTime(startMinutes)}〜${formatTime(parsedEnd)}`;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 時刻を Google カレンダーと同じ「午前9:30」「午後1時」の形にする。ちょうどの時刻は分を省く
 * @graph-connects none
 */
export function formatClockLabel(minutes: number, withPeriod = true): string {
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  const period = hour < 12 ? '午前' : '午後';
  const hour12 = hour <= 12 ? hour : hour - 12;
  const body =
    minute === 0 ? `${String(hour12)}時` : `${String(hour12)}:${String(minute).padStart(2, '0')}`;
  return withPeriod ? `${period}${body}` : body;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 予定の時間帯を Google カレンダーと同じ「午前9:30～10:30」「午前11:30～午後1:45」の形にする。午前・午後が同じなら終わりの側は省く
 * @graph-connects parseTime [calls] 開始・終了の時刻を読む
 * @graph-connects formatClockLabel [calls] 時刻を午前・午後の表記にする
 */
export function formatEventTimeRange(task: Task): string {
  const startMinutes = parseTime(task.startTime);
  if (startMinutes === null) {
    return '';
  }
  const endMinutes = parseTime(task.endTime);
  if (endMinutes === null || endMinutes <= startMinutes) {
    return formatClockLabel(startMinutes);
  }
  const samePeriod = startMinutes < 720 === endMinutes < 720 && endMinutes < 1440;
  return `${formatClockLabel(startMinutes)}～${formatClockLabel(endMinutes, !samePeriod)}`;
}

/**
 * 落とし先。日付と、時間軸なら開始の分。
 */
export interface DropTarget {
  /** 落とした日（`YYYY-MM-DD`） */
  date: string;
  /** 開始の分。終日の帯へ落としたときは持たない */
  startMinutes?: number;
  /** もとの時刻をそのまま持ち越すか。月表示は日付だけを動かすので true にする */
  keepTime?: boolean;
  /** 担当ごとに並べた日表示で、落とした列の担当。別の人の列へ落としたら担当を差し替える */
  assignee?: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 列の中の縦位置を、15 分刻みの開始時刻に丸める。1 日の終わりを超えないように止める
 * @graph-connects none
 */
export function toSlotMinutes(
  offsetPx: number,
  durationMinutes = DEFAULT_DURATION_MINUTES,
): number {
  const raw = Math.round(offsetPx / MINUTE_HEIGHT_PX / SLOT_MINUTES) * SLOT_MINUTES;
  // 終了が翌日へはみ出さないところまで。はみ出すと 1 日の枠に描けなくなる
  const latest = 24 * 60 - durationMinutes;
  return Math.min(Math.max(raw, 0), Math.max(latest, 0));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの長さ（分）を返す。時刻が付いていなければ想定時間から、それも無ければ既定の長さにする
 * @graph-connects parseTime [calls] 時刻を分に直す
 */
export function taskDurationMinutes(task: Task): number {
  const start = parseTime(task.startTime);
  const end = parseTime(task.endTime);
  if (start !== null && end !== null && end > start) {
    return end - start;
  }
  if (
    task.estimatedHours !== null &&
    task.estimatedHours !== undefined &&
    task.estimatedHours > 0
  ) {
    // 想定時間は 0.25 刻みで入っているので、15 分刻みにそのまま乗る
    return Math.round((task.estimatedHours * 60) / SLOT_MINUTES) * SLOT_MINUTES;
  }
  return DEFAULT_DURATION_MINUTES;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 落とした先に合わせてタスクを置き直す。時間軸なら開始と終了を付け、終日の帯なら時刻を外す。担当ごとの列へ落としたら担当も差し替える
 * @graph-connects taskDurationMinutes [calls] タスクの長さを決める
 * @graph-connects formatTime [calls] 分を `HH:MM` に直す
 */
export function moveTask(source: Task, target: DropTarget): Task {
  const task = target.assignee === undefined ? source : { ...source, assignee: target.assignee };
  if (target.keepTime === true) {
    // 月表示から動かしたとき。時刻は触らず、日付だけ差し替える
    return { ...task, dueDate: target.date };
  }
  if (target.startMinutes === undefined) {
    // 終日へ落としたら時刻を外す。undefined にすると API 側で項目が消える
    return { ...task, dueDate: target.date, startTime: undefined, endTime: undefined };
  }
  const duration = taskDurationMinutes(task);
  return {
    ...task,
    dueDate: target.date,
    startTime: formatTime(target.startMinutes),
    endTime: formatTime(Math.min(target.startMinutes + duration, 24 * 60 - 1)),
  };
}

/** つまんだ端。上なら開始、下なら終了を動かす */
export type ResizeEdge = 'start' | 'end';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 端をつまんで伸ばしたタスクの時刻を決める。長さが 15 分を切らないところで止める
 * @graph-connects toSlotMinutes [calls] 位置を 15 分刻みに丸める
 * @graph-connects parseTime [calls] 時刻を分に直す
 * @graph-connects formatTime [calls] 分を `HH:MM` に直す
 */
export function resizeTask(task: Task, edge: ResizeEdge, offsetPx: number): Task {
  const start = parseTime(task.startTime);
  const end = parseTime(task.endTime);
  if (start === null || end === null) {
    // 時刻の無いタスクは端を持てない（終日の帯にいる）
    return task;
  }

  // 丸めの上限は端ごとに違う。開始は終了の手前まで、終了は 23:45 まで。
  // 終了に 24:00 を作ると `parseTime` が読めない値になり、以後その予定は
  // 時刻が消えたものとして扱われる（長さも表示も狂い、直せなくなる）
  const dropped = toSlotMinutes(offsetPx, SLOT_MINUTES);
  if (edge === 'start') {
    // 終了を追い越さない。最短でも 15 分は残す
    return { ...task, startTime: formatTime(Math.min(dropped, end - SLOT_MINUTES)) };
  }
  return { ...task, endTime: formatTime(Math.max(dropped, start + SLOT_MINUTES)) };
}

/** カレンダー上で引いて作った、新しいタスクの下書き */
export interface DraftRange {
  /** 作る日（`YYYY-MM-DD`） */
  date: string;
  /** 開始の分 */
  startMinutes: number;
  /** 終了の分 */
  endMinutes: number;
  /** 担当ごとに並べた日表示で、引いた列の担当 */
  assignee?: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 押した位置と離した位置から、作るタスクの時間帯を決める。上へ引いても下へ引いても同じに扱い、最短 15 分は確保する
 * @graph-connects toSlotMinutes [calls] 位置を 15 分刻みに丸める
 */
export function toDraftRange(date: string, fromPx: number, toPx: number): DraftRange {
  const first = toSlotMinutes(Math.min(fromPx, toPx), SLOT_MINUTES);
  const second = toSlotMinutes(Math.max(fromPx, toPx), SLOT_MINUTES);
  // 押してすぐ離したときも 1 枠ぶんの予定にする（Google カレンダーと同じ）
  const endMinutes = Math.min(Math.max(second, first + SLOT_MINUTES), 24 * 60 - SLOT_MINUTES);
  return { date, startMinutes: first, endMinutes };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 引いた時間帯から、保存する前のタスクを組み立てる。種別と担当は呼び出し側が決める
 * @graph-connects formatTime [calls] 分を `HH:MM` に直す
 */
export function toDraftTask(
  range: DraftRange,
  base: { team: Task['team']; kind: string; assignee: string },
): Omit<Task, 'id'> {
  return {
    team: base.team,
    kind: base.kind,
    title: '',
    // 担当ごとの列で引いたときは、その列の人を担当にする
    assignee: range.assignee ?? base.assignee,
    dueDate: range.date,
    startTime: formatTime(range.startMinutes),
    endTime: formatTime(range.endMinutes),
    status: '未着手',
    source: 'manual',
  };
}
